import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AiGeneratedBadge } from '@/components/AiGeneratedBadge';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, AlertCircle, Target, Activity, Eye, ExternalLink, History } from 'lucide-react';
import { ScoreHistoryInline } from '@/components/ScoreHistoryInline';
import { Link, useLocation } from 'react-router-dom';
import { useDailyToken } from '@/hooks/useDailyToken';
import { ScoreBreakdown } from './ScoreBreakdown';
import { RiskDisclaimer } from './RiskDisclaimer';
import { Skeleton } from '@/components/ui/skeleton';
import SocialShareButtons from './SocialShareButtons';
import OpenGraphMeta from './OpenGraphMeta';
import { NarrativeSummary } from './NarrativeSummary';
import { useLivePrices } from '@/hooks/useLivePrices';

export const DailyTokenPick = () => {
  const { data: dailyToken, isLoading, error } = useDailyToken();
  const { prices: livePrices } = useLivePrices();
  const location = useLocation();

  // The pick's title/OG tags must never hijack the bare homepage. They only
  // apply on a dedicated pick permalink, or when the visitor deep-linked to
  // the pick section itself (/#daily-pick).
  const emitPickMeta =
    location.pathname !== '/' || location.hash === '#daily-pick';

  if (isLoading) {
    return (
      <Card className="gradient-card border-border/50">
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-full mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="gradient-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-destructive" />
            Unable to Load Daily Pick
          </CardTitle>
          <CardDescription>
            There was an error loading today's token pick. Please try again later.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!dailyToken?.success) {
    return (
      <Card className="gradient-card border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-6 w-6 text-muted-foreground" />
            No Strong Pick Today
          </CardTitle>
          <CardDescription>
            {dailyToken?.message || 'No tokens meet our quality threshold today. Check back tomorrow!'}
          </CardDescription>
        </CardHeader>
        {dailyToken?.data?.topCandidate && (
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Highest scoring candidate: <span className="font-semibold">{dailyToken.data.topCandidate.symbol}</span> (Score: {dailyToken.data.topCandidate.score})
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  const { token, score = 0, momentum_tier, momentum_label, rating = 'ON_THE_RADAR', breakdown, position_details, reasons, narrative, risk_level, recommendation, buy_signals, thresholds_used, reviewer } = dailyToken;

  // Resolve a neutral momentum tier label, with backward-compat fallback for legacy `rating`.
  const tierKey = momentum_tier ?? rating;
  const tierLabel = momentum_label ?? (
    tierKey === 'TOP_MOMENTUM' ? 'Top Momentum' :
    tierKey === 'NOTABLE_MOVER' ? 'Notable Mover' :
    tierKey === 'ON_THE_RADAR' ? 'On the Radar' :
    'On the Radar'
  );

  // Format market cap range from thresholds
  const formatMarketCapRange = () => {
    const min = thresholds_used?.min_market_cap || 10_000_000;
    const max = thresholds_used?.max_market_cap || 500_000_000;

    const formatCap = (cap: number) => {
      if (cap >= 1_000_000_000) return `$${(cap / 1_000_000_000).toFixed(0)}B`;
      return `$${(cap / 1_000_000).toFixed(0)}M`;
    };

    return `${formatCap(min)}–${formatCap(max)}`;
  };

  // Prepare sharing data
  const today = new Date().toISOString().split('T')[0];
  const shareUrl = `${window.location.origin}/#daily-pick`;
  const shareTitle = `📊 Today's Top TOSS Mover: ${token.symbol} — Score ${score}/100`;
  const shareDescription = `${token.symbol} is today's top TOSS-scoring token in the small-mid cap range on Australian exchanges. Review the AI Coin Reviewer before considering action.`;
  const hashtags = ['#CryptoAustralia', `#${token.symbol}`, '#TOSS', '#CryptoResearch'];
  const affiliateLinks = [
    '🎯 Swyftx: https://trade.swyftx.com/register/?ref=R6YKMQRQC9CBXAUN',
    '💎 CoinSpot: https://www.coinspot.com.au?affiliate=J2WT2J'
  ];

  // Neutral, muted accent — NOT green/positive-coded. Same styling for all tiers to avoid implying endorsement.
  const tierBadgeClass = 'bg-muted text-muted-foreground border-border';

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: price < 1 ? 4 : 2,
      maximumFractionDigits: price < 1 ? 6 : 2
    }).format(price);
  };

  const formatMarketCap = (marketCap: number) => {
    if (marketCap >= 1_000_000_000) {
      return `$${(marketCap / 1_000_000_000).toFixed(2)}B`;
    } else if (marketCap >= 1_000_000) {
      return `$${(marketCap / 1_000_000).toFixed(2)}M`;
    }
    return `$${marketCap.toLocaleString()}`;
  };

  return (
    <>
      {/* Meta tags for social sharing */}
      {emitPickMeta && (
        <OpenGraphMeta
          title={shareTitle}
          description={shareDescription}
          url={shareUrl}
          type="article"
          publishedTime={today}
          tags={hashtags}
        />
      )}

      <div className="space-y-6">
      <AiGeneratedBadge />
      <Card className="gradient-card border-border/50">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-2xl font-bold flex items-center gap-3">
                <TrendingUp className="h-7 w-7 text-muted-foreground" />
                Today's Top TOSS Mover
              </CardTitle>
              <CardDescription className="mt-2 leading-relaxed">
                Highest-scoring token in the {formatMarketCapRange()} cap range on Australian exchanges. TOSS measures momentum, position, and structural signals — it does not assess entry timing. Review the AI Coin Reviewer below before considering action.
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Badge className={`${tierBadgeClass} text-xs px-2.5 py-1 border whitespace-nowrap`} variant="outline">
                {tierLabel}
              </Badge>
              <div className="flex items-center gap-2">
                <ScoreHistoryInline symbol={token.symbol} />
                <Link
                  to="/track-record"
                  className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary"
                >
                  <History className="w-3 h-3" />
                  ledger
                </Link>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Token Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-3xl font-bold text-foreground">{token.symbol}</div>
              <div className="text-lg text-muted-foreground">{token.name}</div>
            </div>
            {(() => {
              const liveData = livePrices.get(token.symbol?.toUpperCase());
              const livePrice = liveData?.price;
              const entryPrice = token.current_price;
              const pnl = livePrice && entryPrice ? ((livePrice - entryPrice) / entryPrice) * 100 : null;

              return (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Entry Price</div>
                    <div className="font-semibold text-foreground">{formatPrice(entryPrice)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground flex items-center gap-1">
                      Current Price
                      {livePrice && <Badge className="bg-success/20 text-success border-success/30 text-[9px] px-1" variant="outline">Live</Badge>}
                    </div>
                    <div className="font-semibold text-foreground">{livePrice ? formatPrice(livePrice) : formatPrice(entryPrice)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">P&L</div>
                    {pnl !== null ? (
                      <div className={`font-semibold ${pnl >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}%
                      </div>
                    ) : (
                      <div className="font-semibold text-muted-foreground">—</div>
                    )}
                  </div>
                  <div>
                    <div className="text-muted-foreground">Market Cap</div>
                    <div className="font-semibold text-foreground">{formatMarketCap(liveData?.marketCap || token.market_cap)}</div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Narrative Summary - The "Why" Factor */}
          <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
            <NarrativeSummary
              tokenSymbol={token.symbol}
              tokenName={token.name}
              priceChange24h={token.price_change_24h}
              priceChange7d={token.price_change_7d}
              narrative={narrative}
              buySignals={buy_signals}
            />
          </div>

          {/* Entry Timing Read — pulled from latest AI Reviewer cache for this token */}
          {reviewer && (
            <div className="p-4 rounded-lg bg-muted/40 border border-border">
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Eye className="h-4 w-4 text-muted-foreground" />
                  Entry Timing Read
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{reviewer.display_label}</span>
                  {reviewer.overall_score != null && (
                    <Badge variant="outline" className="text-xs">
                      {Math.round(Number(reviewer.overall_score))}/100
                    </Badge>
                  )}
                </div>
              </div>
              {reviewer.headline_reasoning && (
                <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                  "{reviewer.headline_reasoning}"
                </p>
              )}
              <Link
                to={`/coin-reviewer?symbol=${encodeURIComponent(token.symbol)}`}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                View full review
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          )}

          {/* Key Reasons */}
          {reasons && reasons.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Score Breakdown
              </div>
              <ul className="space-y-1.5">
                {reasons.map((reason, idx) => (
                  <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">✓</span>
                    <span>{reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Buy Signals */}
          {buy_signals && buy_signals.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-foreground">Active Signals</div>
              <div className="flex flex-wrap gap-2">
                {buy_signals.map((signal, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {signal.type}: {signal.description}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Narrative */}
          {narrative && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-foreground">Market Sector</div>
              <Badge variant="secondary" className="capitalize">
                {narrative.replace(/_/g, ' ')}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Score Breakdown */}
      <ScoreBreakdown scores={breakdown} positionDetails={position_details} rating={rating} />

      {/* Risk Disclaimer */}
      {breakdown && <RiskDisclaimer score={score} rating={rating} breakdown={breakdown} />}

      {/* Share Section */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold mb-2">
              Share Today's Pick 🚀
            </h3>
            <p className="text-sm text-muted-foreground">
              Help fellow Aussie crypto traders discover this opportunity
            </p>
          </div>
          <div className="flex justify-center">
          <SocialShareButtons
            url={shareUrl}
            title={shareTitle}
            description={shareDescription}
            hashtags={hashtags}
            showLabel={false}
            size="lg"
            affiliateLinks={affiliateLinks}
          />
          </div>
        </CardContent>
      </Card>

      {/* Floating Share Button */}
    </div>
    </>
  );
};
